import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { HttpClientError, RequestStatus } from 'http-client';
import { RootState } from 'app/store';
import CatalogApi from './catalog.api';
import FileAttachmentApi from '../basic/fileAttachment/fileAttachment.api';
import {
    CatalogItem,
    CreateCatalogRequest,
    GetCatalogListRequest,
    UploadCatalogForm,
    UpdateCatalogRequest,
} from './models';

export interface CatalogSlice {
    list?: CatalogItem[];
    single?: CatalogItem;
    versionHistory?: CatalogItem[];
    total?: number;
    rows?: number;
    isLoading?: boolean;
    status?: RequestStatus;
    error?: HttpClientError;
    uploadProgress?: number;
}

const initialState: CatalogSlice = {
    list: [],
    single: undefined,
    versionHistory: [],
    total: 0,
    rows: 0,
    isLoading: false,
    status: undefined,
    error: undefined,
    uploadProgress: 0,
};

/**
 * GET /api/Catalog/ListAll?skip=0&take=10
 */
export const fetchCatalogList = createAsyncThunk(
    'catalog/fetchCatalogList',
    async (args: GetCatalogListRequest, { getState, rejectWithValue }) => {
        try {
            const { userAuth } = (getState() as RootState).user;
            return await CatalogApi.listAll(args, userAuth?.id as string);
        } catch (e) {
            return rejectWithValue(e as HttpClientError);
        }
    },
);

/**
 * GET /api/Catalog/GetById?id=...
 */
export const fetchCatalogDetail = createAsyncThunk(
    'catalog/fetchCatalogDetail',
    async (id: string, { getState, rejectWithValue }) => {
        try {
            const { userAuth } = (getState() as RootState).user;
            return await CatalogApi.getById(id, userAuth?.id as string);
        } catch (e) {
            return rejectWithValue(e as HttpClientError);
        }
    },
);

/**
 * GET /api/Catalog/GetVersionHistory?id=...
 */
export const fetchVersionHistory = createAsyncThunk(
    'catalog/fetchVersionHistory',
    async (id: string, { getState, rejectWithValue }) => {
        try {
            const { userAuth } = (getState() as RootState).user;
            return await CatalogApi.getVersionHistory(
                id,
                userAuth?.id as string,
            );
        } catch (e) {
            return rejectWithValue(e as HttpClientError);
        }
    },
);

/**
 * POST /api/Catalog/ApproveCatalog?id=...
 */
export const approveCatalogStatus = createAsyncThunk(
    'catalog/approveCatalogStatus',
    async (id: string, { getState, rejectWithValue }) => {
        try {
            const { userAuth } = (getState() as RootState).user;
            return await CatalogApi.approveCatalog(id, userAuth?.id as string);
        } catch (e) {
            return rejectWithValue(e as HttpClientError);
        }
    },
);

/**
 * POST /api/Catalog/Create
 */
export const createCatalog = createAsyncThunk(
    'catalog/createCatalog',
    async (data: CreateCatalogRequest, { getState, rejectWithValue }) => {
        try {
            const { userAuth } = (getState() as RootState).user;
            return await CatalogApi.createCatalog(data, userAuth?.id as string);
        } catch (e) {
            return rejectWithValue(e as HttpClientError);
        }
    },
);

/**
 * DELETE /api/Catalog/Delete?id=...
 */
export const deleteCatalogAction = createAsyncThunk(
    'catalog/deleteCatalogAction',
    async (id: string, { getState, rejectWithValue }) => {
        try {
            const { userAuth } = (getState() as RootState).user;
            return await CatalogApi.deleteCatalog(id, userAuth?.id as string);
        } catch (e) {
            return rejectWithValue(e as HttpClientError);
        }
    },
);

/**
 * PUT /api/Catalog/Update
 */
export const updateCatalogAction = createAsyncThunk(
    'catalog/updateCatalogAction',
    async (data: UpdateCatalogRequest, { getState, rejectWithValue }) => {
        try {
            const { userAuth } = (getState() as RootState).user;
            return await CatalogApi.updateCatalog(data, userAuth?.id as string);
        } catch (e) {
            return rejectWithValue(e as HttpClientError);
        }
    },
);

/**
 * Wrapper for uploading a new version of an existing catalog document
 */
export const uploadCatalogVersion = createAsyncThunk(
    'catalog/uploadCatalogVersion',
    async (
        {
            id,
            form,
        }: { id: string; form: { file: File | string; description: string } },
        { dispatch, getState, rejectWithValue },
    ) => {
        try {
            const { single, list, versionHistory } = (getState() as RootState)
                .catalog;
            const currentItem =
                single?.id === id ? single : list?.find((i) => i.id === id);

            let fileUrl = '';
            let originalFileName = '';
            if (typeof form.file === 'string') {
                fileUrl = form.file;
                originalFileName = form.file.split('/').pop() || '';
            } else if (form.file instanceof File) {
                originalFileName = form.file.name;
                const { userAuth } = (getState() as RootState).user;
                const formData = new FormData();
                formData.append('file', form.file);

                const config = {
                    headers: {
                        Authorization: `Bearer ${userAuth?.id as string}`,
                        'Content-Type': 'multipart/form-data',
                    },
                    params: {
                        location: 'catalog',
                    },
                    onUploadProgress: (progressEvent: any) => {
                        if (progressEvent.total) {
                            const percentCompleted = Math.round(
                                (progressEvent.loaded * 100) /
                                    progressEvent.total,
                            );
                            dispatch(
                                setUploadProgress(
                                    Math.min(percentCompleted, 99),
                                ),
                            );
                        }
                    },
                };

                const uploadRes = await FileAttachmentApi.uploadFile(
                    formData,
                    config,
                );
                fileUrl = uploadRes.result;
            } else {
                fileUrl =
                    'https://qms-media.ultimosolution.com/Media/File/2026/07/08dd36b1-0b61-42c7-a6a1-a22feb819eca.xlsx';
            }

            const categoryNum =
                typeof currentItem?.category === 'number'
                    ? currentItem.category
                    : parseInt(String(currentItem?.category), 10) || 0;

            const latestHistoryVer =
                versionHistory && versionHistory.length > 0
                    ? versionHistory[0].version
                    : null;
            const currentVer =
                latestHistoryVer || currentItem?.version || 'v1.0';
            const verNum =
                parseFloat(currentVer.replace(/[^0-9.]/g, '')) || 1.0;
            const nextVerStr = `${(verNum + 0.1).toFixed(1)}`;

            const createPayload: CreateCatalogRequest = {
                title: currentItem?.title || 'Catalog Document',
                description: form.description,
                category: categoryNum,
                teamId: currentItem?.teamId || '',
                version: nextVerStr,
                file: fileUrl,
                originalFileName,
                status: 0,
            };

            const actionResult = await dispatch(createCatalog(createPayload));
            return actionResult.payload;
        } catch (e) {
            return rejectWithValue(e as HttpClientError);
        }
    },
);

/**
 * Wrapper for UI form upload matching POST /api/Catalog/Create
 */
export const uploadNewCatalog = createAsyncThunk(
    'catalog/uploadNewCatalog',
    async (
        args:
            | { form: UploadCatalogForm; groupName?: string }
            | CreateCatalogRequest,
        { dispatch, getState, rejectWithValue },
    ) => {
        try {
            let createPayload: CreateCatalogRequest;

            if ('title' in args && 'teamId' in args) {
                createPayload = args as CreateCatalogRequest;
            } else {
                const { form } = args as {
                    form: UploadCatalogForm;
                    groupName?: string;
                };

                let fileUrl = '';
                let originalFileName = '';
                if (typeof form.file === 'string') {
                    fileUrl = form.file;
                    originalFileName = form.file.split('/').pop() || '';
                } else if (form.file instanceof File) {
                    originalFileName = form.file.name;
                    const { userAuth } = (getState() as RootState).user;
                    const formData = new FormData();
                    formData.append('file', form.file);

                    const config = {
                        headers: {
                            Authorization: `Bearer ${userAuth?.id as string}`,
                            'Content-Type': 'multipart/form-data',
                        },
                        params: {
                            location: 'catalog',
                        },
                        onUploadProgress: (progressEvent: any) => {
                            if (progressEvent.total) {
                                const percentCompleted = Math.round(
                                    (progressEvent.loaded * 100) /
                                        progressEvent.total,
                                );
                                dispatch(
                                    setUploadProgress(
                                        Math.min(percentCompleted, 99),
                                    ),
                                );
                            }
                        },
                    };

                    const uploadRes = await FileAttachmentApi.uploadFile(
                        formData,
                        config,
                    );
                    fileUrl = uploadRes.result;
                } else {
                    fileUrl =
                        'https://qms-media.ultimosolution.com/Media/File/2026/07/08dd36b1-0b61-42c7-a6a1-a22feb819eca.xlsx';
                }

                const catNum =
                    typeof form.category === 'number'
                        ? form.category
                        : parseInt(form.category as string, 10) || 0;

                createPayload = {
                    title: form.document_title,
                    description: form.description,
                    category: catNum,
                    teamId: form.group_id,
                    version: '1',
                    file: fileUrl,
                    originalFileName,
                    status: 0,
                };
            }

            const actionResult = await dispatch(createCatalog(createPayload));
            return actionResult.payload;
        } catch (e) {
            return rejectWithValue(e as HttpClientError);
        }
    },
);

const catalogSlice = createSlice({
    name: 'catalog',
    initialState,
    reducers: {
        resetCatalogStatus: (state) => {
            state.status = undefined;
            state.error = undefined;
        },
        setUploadProgress: (state, action) => {
            state.uploadProgress = action.payload;
        },
    },
    extraReducers: (builder) => {
        // fetchCatalogList
        builder.addCase(fetchCatalogList.pending, (state) => {
            state.isLoading = true;
            state.error = undefined;
        });
        builder.addCase(fetchCatalogList.fulfilled, (state, { payload }) => {
            state.isLoading = false;
            if (payload && payload.data) {
                state.list = payload.data.data ?? [];
                state.total = payload.data.total ?? 0;
                state.rows = payload.data.total ?? 0;
            }
        });
        builder.addCase(fetchCatalogList.rejected, (state, action) => {
            state.isLoading = false;
            state.error = action.payload as HttpClientError;
        });

        // fetchCatalogDetail
        builder.addCase(fetchCatalogDetail.pending, (state) => {
            state.isLoading = true;
            state.error = undefined;
        });
        builder.addCase(fetchCatalogDetail.fulfilled, (state, { payload }) => {
            state.isLoading = false;
            if (payload && payload.data) {
                state.single = payload.data;
            }
        });
        builder.addCase(fetchCatalogDetail.rejected, (state, action) => {
            state.isLoading = false;
            state.error = action.payload as HttpClientError;
        });

        // fetchVersionHistory
        builder.addCase(fetchVersionHistory.pending, (state) => {
            state.isLoading = true;
            state.error = undefined;
        });
        builder.addCase(
            fetchVersionHistory.fulfilled,
            (state, { payload, meta }) => {
                state.isLoading = false;
                if (payload && payload.data) {
                    state.versionHistory = payload.data;
                    if (state.single && state.single.id === meta.arg) {
                        state.single.versions = payload.data;
                    }
                }
            },
        );
        builder.addCase(fetchVersionHistory.rejected, (state, action) => {
            state.isLoading = false;
            state.error = action.payload as HttpClientError;
        });

        // approveCatalogStatus
        builder.addCase(approveCatalogStatus.pending, (state) => {
            state.isLoading = true;
            state.status = RequestStatus.pending;
        });
        builder.addCase(approveCatalogStatus.fulfilled, (state, action) => {
            state.isLoading = false;
            state.status = RequestStatus.success;
            const targetId = action.meta.arg;
            if (state.list) {
                const idx = state.list.findIndex((i) => i.id === targetId);
                if (idx !== -1) {
                    state.list[idx].status = 1;
                }
            }
            if (state.single && state.single.id === targetId) {
                state.single.status = 1;
            }
        });
        builder.addCase(approveCatalogStatus.rejected, (state, action) => {
            state.isLoading = false;
            state.status = RequestStatus.failed;
            state.error = action.payload as HttpClientError;
        });

        // createCatalog
        builder.addCase(createCatalog.pending, (state) => {
            state.isLoading = true;
            state.status = RequestStatus.pending;
            state.error = undefined;
            state.uploadProgress = 30;
        });
        builder.addCase(createCatalog.fulfilled, (state, { payload }) => {
            state.isLoading = false;
            state.status = RequestStatus.success;
            state.uploadProgress = 100;
            const createdItem = (payload as any)?.data || payload;
            if (createdItem && createdItem.id && state.list) {
                state.list = [createdItem, ...state.list];
                state.total = (state.total || 0) + 1;
                state.rows = (state.rows || 0) + 1;
            }
        });
        builder.addCase(createCatalog.rejected, (state, action) => {
            state.isLoading = false;
            state.status = RequestStatus.failed;
            state.error = action.payload as HttpClientError;
            state.uploadProgress = 0;
        });

        // uploadNewCatalog
        builder.addCase(uploadNewCatalog.pending, (state) => {
            state.isLoading = true;
            state.status = RequestStatus.pending;
            state.error = undefined;
            state.uploadProgress = 30;
        });
        builder.addCase(uploadNewCatalog.fulfilled, (state) => {
            state.isLoading = false;
            state.status = RequestStatus.success;
            state.uploadProgress = 100;
        });
        builder.addCase(uploadNewCatalog.rejected, (state, action) => {
            state.isLoading = false;
            state.status = RequestStatus.failed;
            state.error = action.payload as HttpClientError;
            state.uploadProgress = 0;
        });

        // uploadCatalogVersion
        builder.addCase(uploadCatalogVersion.pending, (state) => {
            state.isLoading = true;
            state.status = RequestStatus.pending;
            state.error = undefined;
            state.uploadProgress = 30;
        });
        builder.addCase(uploadCatalogVersion.fulfilled, (state) => {
            state.isLoading = false;
            state.status = RequestStatus.success;
            state.uploadProgress = 100;
        });
        builder.addCase(uploadCatalogVersion.rejected, (state, action) => {
            state.isLoading = false;
            state.status = RequestStatus.failed;
            state.error = action.payload as HttpClientError;
            state.uploadProgress = 0;
        });

        // deleteCatalogAction
        builder.addCase(deleteCatalogAction.pending, (state) => {
            state.isLoading = true;
            state.status = RequestStatus.pending;
        });
        builder.addCase(deleteCatalogAction.fulfilled, (state, action) => {
            state.isLoading = false;
            state.status = RequestStatus.success;
            const targetId = action.meta.arg;
            if (state.list) {
                state.list = state.list.filter((i) => i.id !== targetId);
                state.total = Math.max(0, (state.total || 0) - 1);
                state.rows = Math.max(0, (state.rows || 0) - 1);
            }
        });
        builder.addCase(deleteCatalogAction.rejected, (state, action) => {
            state.isLoading = false;
            state.status = RequestStatus.failed;
            state.error = action.payload as HttpClientError;
        });

        // updateCatalogAction
        builder.addCase(updateCatalogAction.pending, (state) => {
            state.isLoading = true;
            state.status = RequestStatus.pending;
            state.error = undefined;
        });
        builder.addCase(updateCatalogAction.fulfilled, (state, { payload }) => {
            state.isLoading = false;
            state.status = RequestStatus.success;
            const updatedItem = (payload as any)?.data || payload;
            if (updatedItem && updatedItem.id) {
                if (state.single?.id === updatedItem.id) {
                    state.single = { ...state.single, ...updatedItem };
                }
                if (state.list) {
                    const idx = state.list.findIndex(
                        (i) => i.id === updatedItem.id,
                    );
                    if (idx !== -1) {
                        state.list[idx] = {
                            ...state.list[idx],
                            ...updatedItem,
                        };
                    }
                }
            }
        });
        builder.addCase(updateCatalogAction.rejected, (state, action) => {
            state.isLoading = false;
            state.status = RequestStatus.failed;
            state.error = action.payload as HttpClientError;
        });
    },
});

export const { resetCatalogStatus, setUploadProgress } = catalogSlice.actions;
export default catalogSlice.reducer;
